import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBOAuth } from '../../context/BOAuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { boAPI } from '../../lib/api';
import { BOLayout } from './BOLayout';
import {
  Users, MapPin, ShoppingCart, CreditCard,
  TrendingUp, TrendingDown, Building2, ArrowRight,
  DollarSign, AlertTriangle,
} from 'lucide-react';

const formatNaira = (amount) => new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 }).format(amount || 0);

const StatCard = ({ title, value, subtitle, icon: Icon, color = 'bg-primary-100 text-primary-700' }) => (
  <Card className="border-neutral-border shadow-card">
    <CardContent className="p-5">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-neutral-500">{title}</p>
          <p className="text-xl font-bold mt-1">{value}</p>
          {subtitle && <p className="text-xs text-neutral-400 mt-0.5">{subtitle}</p>}
        </div>
        <div className={`w-10 h-10 rounded-xl ${color} flex items-center justify-center`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const BODashboard = () => {
  const { user, role } = useBOAuth();
  const navigate = useNavigate();
  const [dashData, setDashData] = useState(null);
  const [salesData, setSalesData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        if (role === 'owner') {
          const res = await boAPI.getFinancialDashboard();
          setDashData(res.data);
        }
        const salesRes = await boAPI.getSales();
        setSalesData(salesRes.data);
      } catch (e) { console.error('BODashboard fetch failed:', e); }
      finally { setLoading(false); }
    };
    fetch();
  }, [role]);

  const totalSalesValue = salesData?.sales?.reduce((s, sale) => s + (sale.total_amount || 0), 0) || 0;
  const totalCollected = salesData?.sales?.reduce((s, sale) => s + (sale.total_paid || 0), 0) || 0;
  const activeSalesCount = salesData?.sales?.filter(s => s.payment_status === 'active').length || 0;

  return (
    <BOLayout>
      <div className="space-y-6" data-testid="bo-dashboard">
        <div>
          <h1 className="text-3xl sm:text-4xl font-sans font-semibold tracking-tight text-neutral-900">
            Welcome back, <span className="font-bold">{user?.full_name?.split(' ')[0]}</span>
          </h1>
          <p className="text-neutral-500 mt-1">
            {role === 'owner' ? 'Business Overview' : role === 'sales_admin' ? 'Sales Overview' : 'Financial Overview'}
          </p>
        </div>

        {/* Owner Dashboard */}
        {role === 'owner' && dashData && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard title="Total Revenue" value={formatNaira(dashData.total_revenue)} subtitle={`This month: ${formatNaira(dashData.month_revenue)}`} icon={TrendingUp} color="bg-green-100 text-green-700" />
              <StatCard title="Total Expenses" value={formatNaira(dashData.total_expenses)} subtitle={`This month: ${formatNaira(dashData.month_expenses)}`} icon={TrendingDown} color="bg-red-100 text-red-700" />
              <StatCard title="Net Profit" value={formatNaira(dashData.total_profit)} subtitle={`This month: ${formatNaira(dashData.month_profit)}`} icon={DollarSign} color={dashData.total_profit >= 0 ? 'bg-primary-100 text-primary-700' : 'bg-red-100 text-red-700'} />
              <StatCard title="Outstanding" value={formatNaira(dashData.outstanding)} subtitle={`${dashData.active_sales} active sales`} icon={AlertTriangle} color="bg-amber-100 text-amber-700" />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <StatCard title="Active Clients" value={dashData.total_clients} icon={Users} color="bg-primary-100 text-primary-700" />
              <StatCard title="Plots Sold" value={`${dashData.sold_plots}/${dashData.total_plots}`} icon={MapPin} color="bg-blue-100 text-blue-700" />
              <StatCard title="Collection Rate" value={`${dashData.total_sales_value > 0 ? Math.round((dashData.total_revenue / dashData.total_sales_value) * 100) : 0}%`} icon={CreditCard} color="bg-teal-100 text-teal-700" />
              <Card className="border-neutral-border shadow-card cursor-pointer hover:border-primary-300 transition-colors" onClick={() => navigate('/backoffice/financial')}>
                <CardContent className="p-5 flex items-center justify-between">
                  <div>
                    <p className="text-xs text-neutral-500">View Full</p>
                    <p className="text-lg font-bold mt-1">Financials</p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-primary-600" />
                </CardContent>
              </Card>
            </div>
          </>
        )}

        {/* Sales Admin Dashboard */}
        {role === 'sales_admin' && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <StatCard title="Total Sales Value" value={formatNaira(totalSalesValue)} icon={ShoppingCart} color="bg-primary-100 text-primary-700" />
            <StatCard title="Total Collected" value={formatNaira(totalCollected)} icon={TrendingUp} color="bg-green-100 text-green-700" />
            <StatCard title="Active Sales" value={activeSalesCount} icon={CreditCard} color="bg-amber-100 text-amber-700" />
          </div>
        )}

        {/* Accountant Dashboard */}
        {role === 'accountant' && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <StatCard title="Total Collected" value={formatNaira(totalCollected)} icon={CreditCard} color="bg-green-100 text-green-700" />
            <StatCard title="Outstanding" value={formatNaira(totalSalesValue - totalCollected)} icon={AlertTriangle} color="bg-amber-100 text-amber-700" />
            <StatCard title="Total Sales" value={salesData?.total || 0} icon={ShoppingCart} color="bg-primary-100 text-primary-700" />
          </div>
        )}

        {/* Quick Actions */}
        {loading && (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-600" />
          </div>
        )}

        {!loading && (
          <Card className="border-neutral-border shadow-card">
            <CardHeader><CardTitle className="text-lg">Quick Actions</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <button onClick={() => navigate('/backoffice/clients')} className="p-4 rounded-xl border border-neutral-200 hover:border-primary-300 hover:bg-primary-50 transition-colors text-left" data-testid="quick-action-clients">
                  <Users className="h-5 w-5 text-primary-600 mb-2" />
                  <p className="font-medium text-sm">Clients</p>
                  <p className="text-xs text-neutral-500">Manage clients</p>
                </button>
                <button onClick={() => navigate('/backoffice/plots')} className="p-4 rounded-xl border border-neutral-200 hover:border-primary-300 hover:bg-primary-50 transition-colors text-left" data-testid="quick-action-plots">
                  <MapPin className="h-5 w-5 text-primary-600 mb-2" />
                  <p className="font-medium text-sm">Plots</p>
                  <p className="text-xs text-neutral-500">View plots</p>
                </button>
                <button onClick={() => navigate('/backoffice/sales')} className="p-4 rounded-xl border border-neutral-200 hover:border-primary-300 hover:bg-primary-50 transition-colors text-left" data-testid="quick-action-sales">
                  <ShoppingCart className="h-5 w-5 text-primary-600 mb-2" />
                  <p className="font-medium text-sm">Sales</p>
                  <p className="text-xs text-neutral-500">View sales</p>
                </button>
                <button onClick={() => navigate('/backoffice/payments')} className="p-4 rounded-xl border border-neutral-200 hover:border-primary-300 hover:bg-primary-50 transition-colors text-left" data-testid="quick-action-payments">
                  <CreditCard className="h-5 w-5 text-primary-600 mb-2" />
                  <p className="font-medium text-sm">Payments</p>
                  <p className="text-xs text-neutral-500">Track payments</p>
                </button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </BOLayout>
  );
};

export default BODashboard;
